/**
 * A miniature of Friday, painted in a theme's own tokens.
 *
 * The theme picker shows the product as it will look, not an abstract swatch:
 * the side rail, the canvas and a few task cards, all in the theme's colours,
 * its corner shape and its typeface. That way the picker answers "what will
 * Friday look like?" before the user commits to a theme.
 *
 * The miniature is about 280px wide, so every measurement here is in pixels —
 * percentages would resolve against the whole preview and blow the narrow
 * side rail apart.
 */

import type { ThemeDefinition } from '@/features/theme/themes'

export function ThemePreview({ theme }: { theme: ThemeDefinition }) {
  const t = theme.tokens
  const rgb = (token: string) => `rgb(${token})`

  return (
    <div
      className="w-full overflow-hidden border-b"
      style={{ backgroundColor: rgb(t.bg), borderColor: rgb(t.border) }}
    >
      <div className="flex" style={{ aspectRatio: '16 / 10' }}>
        {/* The side rail. */}
        <div
          className="flex flex-col gap-[5px] p-[7px]"
          style={{
            width: '30%',
            backgroundColor: rgb(t.bgSubtle),
            borderRight: `1px solid ${rgb(t.border)}`,
          }}
        >
          <div
            className="h-[7px] w-[62%]"
            style={{ backgroundColor: rgb(t.accent), borderRadius: t.radiusSm }}
          />
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-[4px] w-[78%]"
              style={{
                backgroundColor: rgb(t.fgMuted),
                opacity: i === 0 ? 0.7 : 0.32,
                borderRadius: t.radiusSm,
              }}
            />
          ))}
        </div>

        {/* The canvas. */}
        <div className="flex flex-1 flex-col p-[9px]" style={{ backgroundColor: rgb(t.bg) }}>
          <div className="flex items-center gap-[5px]">
            <span
              style={{
                fontFamily: t.fontFamily ?? 'Inter, system-ui, sans-serif',
                fontSize: 10,
                fontWeight: 700,
                lineHeight: 1,
                color: rgb(t.fg),
              }}
            >
              Aa
            </span>
            <span
              className="h-[5px] w-[36%]"
              style={{ backgroundColor: rgb(t.fgMuted), opacity: 0.55, borderRadius: t.radiusSm }}
            />
          </div>

          <div className="mt-[9px] grid flex-1 grid-cols-3 items-start gap-[5px]">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex flex-col gap-[4px]">
                <div
                  className="h-[4px] w-[45%]"
                  style={{ backgroundColor: rgb(t.fgMuted), opacity: 0.5, borderRadius: t.radiusSm }}
                />
                <div
                  className="flex flex-col gap-[3px] p-[4px]"
                  style={{
                    backgroundColor: rgb(t.bgElevated),
                    border: `1px solid ${rgb(t.border)}`,
                    borderRadius: t.radiusSm,
                  }}
                >
                  <div
                    className="h-[4px] w-[75%]"
                    style={{ backgroundColor: rgb(t.fgMuted), opacity: 0.6, borderRadius: t.radiusSm }}
                  />
                  <div
                    className="h-[4px] w-[45%]"
                    style={{ backgroundColor: rgb(t.fgMuted), opacity: 0.3, borderRadius: t.radiusSm }}
                  />
                  <div
                    className="mt-[2px] h-[5px] w-[16px]"
                    style={{ backgroundColor: rgb(t.accent), borderRadius: t.radiusSm }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
